import React, { useState } from "react";
import {
  Button,
  CustomDropdown,
  DropdownOption,
  Modal,
  TrashIcon,
} from "../../../components/ui";
import { fromMealTimeParts, toMealTimeParts } from "../planMapping";
import type { MealSlotTypeOption, MealSlot } from "../types/plans.types";
import type { MealSlotTypesStatus } from "../hooks/useMealSlotTypes";

const toOptions = (values: string[]): DropdownOption[] =>
  values.map((value) => ({ value, label: value }));

// The three lists the time is picked from. Built at module scope: they are the
// same twelve hours and sixty minutes every time the dialog opens.
const HOUR_OPTIONS = toOptions(
  Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"))
);
const MINUTE_OPTIONS = toOptions(
  Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"))
);
const PERIOD_OPTIONS = toOptions(["AM", "PM"]);

export interface MealSlotModalProps {
  /** The meal being edited. Left out when a new one is being added. */
  meal?: MealSlot;
  /**
   * Names the plan already has, the meal being edited excluded. They are kept
   * off the list: a day has one breakfast, and two meals of the same name
   * could not be told apart on the pills.
   */
  takenNames: string[];
  /**
   * What a meal can be called, from `catalog.mealSlotTypes`. Passed in rather
   * than fetched here: the builder needs the same list to know when the day
   * has no names left to add, and one request between them beats two.
   */
  slotTypes: MealSlotTypeOption[];
  slotTypesStatus: MealSlotTypesStatus;
  onClose: () => void;
  /**
   * Given the answers, and the id of the slot type behind the name — which is
   * what adding or renaming a meal sends. Saving is the caller's to do, and it
   * is a request now, so this may be awaited: the dialog stays up and usable
   * until it resolves.
   */
  onSave: (
    answers: { name: string; time: string; slotTypeId: number }
  ) => void | Promise<void>;
  /** Offered while editing only — a meal being added has nothing to remove. */
  onRemove?: () => void | Promise<void>;
}

/**
 * Adding a meal to the day, and updating one already on it — the same two
 * questions either way, so the same dialog does both.
 *
 * Neither control is labelled: a dialog called "Add new meal" holding a meal
 * name and a time has nothing left to explain, and the design leaves the rows
 * to speak for themselves. The names are still there for screen readers.
 *
 * The time is three dropdowns rather than a `type="time"` input: the native
 * control opens the browser's own picker, which is styled by the browser, sits
 * outside the dialog's own look, and differs on every platform.
 */
const MealSlotModal: React.FC<MealSlotModalProps> = ({
  meal,
  takenNames,
  slotTypes,
  slotTypesStatus: typesStatus,
  onClose,
  onSave,
  onRemove,
}) => {
  const [name, setName] = useState(meal?.name ?? "");

  // A meal being edited opens on its own time; a new one opens empty, so the
  // hour is chosen rather than inherited from whatever this file defaulted to.
  const initial = meal ? toMealTimeParts(meal.time) : null;
  const [hour, setHour] = useState(initial?.hour ?? "");
  const [minute, setMinute] = useState(initial?.minute ?? "");
  const [period, setPeriod] = useState<string>(initial?.period ?? "AM");
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  // Adding, renaming and removing are all requests now, and a second one
  // would make, or unmake, a second meal.
  const [saving, setSaving] = useState(false);

  const editing = !!meal;

  // A short enough list to filter on every render, and doing it here keeps it
  // honest: `takenNames` is a new array each time the page renders, so a memo
  // on it would never hit anyway.
  const nameOptions: DropdownOption[] = slotTypes
    .filter((type) => !takenNames.includes(type.name))
    .map((type) => ({ value: String(type.id), label: type.name }));

  /**
   * The id behind the meal's own name.
   *
   * A meal read back from the plan carries the label but not the id — the
   * detail endpoint sends no `meal_slot_type` — so it is found by matching
   * the name against the catalog the dialog has just fetched.
   */
  const currentTypeId =
    meal?.slotTypeId ??
    slotTypes.find((type) => type.name === meal?.name)?.id ??
    null;

  // A meal whose name is off the list — older data, or a name since retired —
  // still has to be selectable, or editing its time would silently rename it.
  if (
    meal &&
    !nameOptions.some((option) => option.label === meal.name) &&
    meal.name
  ) {
    nameOptions.unshift({
      value: currentTypeId == null ? meal.name : String(currentTypeId),
      label: meal.name,
    });
  }

  // Held as the id the save sends, so the value is what travels rather than
  // something looked up again from the label at the last moment.
  const [slotTypeId, setSlotTypeId] = useState<number | null>(null);
  const selectedValue =
    slotTypeId != null ? String(slotTypeId) : currentTypeId != null ? String(currentTypeId) : name;

  const time = hour && minute
    ? fromMealTimeParts({ hour, minute, period: period === "PM" ? "PM" : "AM" })
    : "";

  // Both questions answered — and, when editing, answered differently from how
  // they already stand. Updating a meal to exactly what it was is not an edit.
  // A name with no id behind it cannot be sent, so it does not count as one.
  const resolvedTypeId = slotTypeId ?? currentTypeId;
  const complete = !!name && !!time && resolvedTypeId != null;
  const changed = !editing || name !== meal?.name || time !== meal?.time;

  const handleSave = async () => {
    if (resolvedTypeId == null || saving) return;
    setSaving(true);
    try {
      await onSave({ name, time, slotTypeId: resolvedTypeId });
    } finally {
      // The dialog is closed by the caller on success, so this only ever runs
      // after a failure — which is exactly when it has to be usable again.
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!onRemove || saving) return;
    setSaving(true);
    try {
      await onRemove();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title={editing ? "Update meal" : "Add new meal"}
      onClose={onClose}
      className="max-w-[526px]"
      footer={
        confirmingRemoval && onRemove ? (
          // The same question the option card asks, in the same words: one
          // thing on screen to answer, so the other two buttons stand down.
          <div className="flex w-full animate-confirm-in items-center gap-3">
            <Button
              variant="danger"
              size="md"
              onClick={handleRemove}
              disabled={saving}
              className="whitespace-nowrap"
            >
              {saving ? "Deleting…" : "Confirm delete"}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setConfirmingRemoval(false)}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <>
            {onRemove && (
              <button
                type="button"
                aria-label="Remove meal"
                onClick={() => setConfirmingRemoval(true)}
                // Pushed away from the two buttons it must not be pressed by
                // mistake for.
                className="mr-auto rounded-md p-2 text-[#FF3B30] transition-colors hover:bg-[#FDECEC]"
              >
                <TrashIcon className="h-[20px] w-[20px]" />
              </button>
            )}
            <Button
              variant="secondary"
              size="md"
              disabled={saving}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              size="md"
              disabled={!complete || !changed || saving}
              onClick={handleSave}
            >
              {saving
                ? editing
                  ? "Updating…"
                  : "Adding…"
                : editing
                ? "Update meal"
                : "Add meal"}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {/* `CustomDropdown` rather than `DropdownField`: the row has to carry
            the slot type's id, and the labelled field only deals in strings.
            Adding a meal sends `meal_slot_type: 2`, not "Morning Tea". */}
        <CustomDropdown
          aria-label="Meal name"
          value={selectedValue}
          onChange={(value) => {
            const id = Number(value);
            setSlotTypeId(id);
            setName(
              nameOptions.find((option) => option.value === value)?.label ?? ""
            );

            // Lunch is usually at half one, so say so — the catalog carries
            // the usual time for each name. Only when adding, and only into a
            // time not yet set: a meal being edited is on the plan at the
            // hour it was put there, and moving it is the other reason this
            // dialog is open.
            if (editing) return;
            const usual = slotTypes.find((type) => type.id === id)?.defaultTime;
            const parts = usual ? toMealTimeParts(usual) : null;
            if (!parts || hour || minute) return;
            setHour(parts.hour);
            setMinute(parts.minute);
            setPeriod(parts.period);
          }}
          placeholder={
            typesStatus === "loading"
              ? "Loading meal names…"
              : typesStatus === "failed"
              ? "Could not load meal names"
              : "Select meal"
          }
          disabled={typesStatus !== "ready"}
          options={nameOptions}
        />

        {/* Three controls, one answer between them. */}
        <div role="group" aria-label="Time" className="flex items-center gap-3">
          <CustomDropdown
            className="flex-1"
            options={HOUR_OPTIONS}
            value={hour}
            onChange={setHour}
            placeholder="hr."
            maxHeight={280}
            aria-label="Hour"
          />
          <CustomDropdown
            className="flex-1"
            options={MINUTE_OPTIONS}
            value={minute}
            onChange={setMinute}
            placeholder="min."
            maxHeight={280}
            aria-label="Minute"
          />
          <CustomDropdown
            className="flex-1"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
            placeholder="AM"
            maxHeight={280}
            aria-label="AM or PM"
          />
        </div>
      </div>
    </Modal>
  );
};

export default MealSlotModal;
